import type { CampaignJob } from "@/api/hooks/campaigns"

/**
 * Job queue panel. Parity: `jobs.js:23-67`.
 *
 *   - Max 20 rows (the source slices to 20).
 *   - Status icons: ✓ completed, ✗ failed, spinner processing, ● pending.
 *   - Stale warning after 5 min while still "processing".
 *   - Error messages truncated to 1000 chars (bumped from 120 per
 *     recent memory; confirmed in parity doc).
 *   - `created_at` rendered as local time.
 */

const MAX_ROWS = 50
const STALE_THRESHOLD_MS = 5 * 60 * 1000

function isStale(job: CampaignJob): boolean {
  if (job.status !== "processing" || !job.started_at) return false
  const started = new Date(job.started_at).getTime()
  if (Number.isNaN(started)) return false
  return Date.now() - started > STALE_THRESHOLD_MS
}

function statusIcon(status: string) {
  switch (status) {
    case "completed":
      return <span className="text-emerald-400">✓</span>
    case "failed":
      return <span className="text-red-400">✗</span>
    case "processing":
      return (
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
      )
    default:
      return <span className="text-white">●</span>
  }
}

export function JobQueuePanel({
  jobs,
  className = "",
  listClassName = "",
}: {
  jobs: CampaignJob[]
  className?: string
  listClassName?: string
}) {
  return (
    <div
      className={`rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.22)] ${className}`.trim()}
    >
      {jobs.length === 0 ? (
        <div className="py-4 text-center text-sm text-white">
          No campaigns in queue
        </div>
      ) : (
        <div className={`max-h-[360px] space-y-2 overflow-y-auto pr-1 ${listClassName}`.trim()}>
          {jobs.slice(0, MAX_ROWS).map((j) => {
            const time = j.created_at
              ? new Date(j.created_at).toLocaleTimeString()
              : ""
            const error = j.error_message?.substring(0, 1000)
            return (
              <div
                key={j.job_id}
                className={`rounded-md border border-[#4a4a4a] bg-[#292929] p-2.5 job-status-${j.status}`}
              >
                <div className="flex items-center gap-2 text-sm">
                  <span>{statusIcon(j.status)}</span>
                  <span className="flex-1 truncate font-medium">
                    {j.campaign_name}
                  </span>
                  <span className="text-xs text-white">{time}</span>
                </div>
                {j.campaign_id ? (
                  <div className="mt-1 text-xs text-white">
                    ID: {j.campaign_id}
                  </div>
                ) : null}
                {error ? (
                  <div className="mt-1 whitespace-pre-wrap text-xs text-red-400">
                    {error}
                  </div>
                ) : null}
                {isStale(j) ? (
                  <div className="mt-1 text-xs text-amber-400">
                    Taking longer than expected — will auto-recover shortly.
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
